import { i18nFn } from '../../../types';
import DateUtils from '../../../utils/DateUtils';
import StringUtils from '../../../utils/StringUtils';
import { SCORE_MAX } from '../constants';
import {
  AppointmentCategoryKey,
  EAppointmentStatus,
  EPersonType,
  ERiskLevel,
  IAppointmentCategory,
  IAppointmentDetailRecord,
  IAppointmentDetailSection,
  ICreditLimit,
  ICreditScoreData,
  IMetricCard,
  IRiskAnalysis
} from '../subcomponents/score-dashboard/subcomponents/score-result/types';
import {
  IGetLatestScoreByDocumentResponse,
  IScoreCivilActionDTO,
  IScoreProtestDTO,
  IScoreRestrictionDTO,
  IScoreSummaryResponse
} from '../types/api';
import { deriveBusinessRiskLevel, deriveRiskLevelFromScore } from './deriveRiskLevel';
import { IScoreBand, resolveScoreBand } from './resolveScoreBand';
import { deriveAlerts } from './deriveAlerts';


export interface MapToCreditScoreDataInput {
  score: IGetLatestScoreByDocumentResponse;
  summary: IScoreSummaryResponse | null;
}

interface MetricCardConfig {
  icon: string;
  i18nKey: string;
  getValue: (summary: IScoreSummaryResponse | null, i18n: i18nFn) => string;
}

interface BuildAppointmentCategoryParams {
  icon: string;
  categoryKey: AppointmentCategoryKey;
  i18n: i18nFn;
  scoreResponse: IGetLatestScoreByDocumentResponse;
}

const METRIC_CARDS: MetricCardConfig[] = [
  {
    icon: 'money-stack',
    i18nKey: 'averageTicket',
    getValue: summary => StringUtils.formatBRL(summary?.averageTicket ?? 0),
  },
  {
    icon: 'exclamation-circle',
    i18nKey: 'delinquencyRate',
    getValue: summary => StringUtils.formatPercent(summary?.delinquenceyRate ?? 0, 0),
  },
  {
    icon: 'bill',
    i18nKey: 'averageMonthlySales',
    getValue: summary => (summary?.averageMonthlySales ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 }),
  },
  {
    icon: 'calendar-alt',
    i18nKey: 'averageDaysToCollect',
    getValue: (summary, i18n) =>
      i18n('FinBankCreditScoreResult.metricCards.averageDaysToCollect.value', {
        days: (summary?.averageDaysToCollect ?? 0).toFixed(0),
      }),
  },
];

const MAX_RECENT_DETAIL_RECORDS_PF = 10;
const MAX_RECENT_DETAIL_RECORDS_PJ = 5;

const SCORE_UNAVAILABLE_CODE = 'YYYY';

function isScoreUnavailable(rawScore: string | number | null | undefined): boolean {
  return String(rawScore ?? '').trim().toUpperCase() === SCORE_UNAVAILABLE_CODE;
}



const APPOINTMENT_CATEGORIES: Array<{ key: AppointmentCategoryKey; icon: string;  }> = [
  { key: 'debts', icon: 'file-exclamation'  },
  { key: 'civilActions' , icon: 'user-circle' },
  { key: 'protests', icon: 'file-copy' },
];

export function mapToCreditScoreData(input: MapToCreditScoreDataInput, i18n: i18nFn): ICreditScoreData {
  const { score, summary } = input;
  const isCompany = isCompanyDocument(score.score.documentType);
  const scoreUnavailable = isScoreUnavailable(score.score.scoreValue);
  const scoreValue = StringUtils.safeParseInt(score.score.scoreValue);
  const scoreBand = resolveScoreBand(isCompany, scoreValue);
  const gaugeRiskLevel = deriveRiskLevelFromScore(scoreValue);
  const probabilityAvailable = scoreBand.probability !== null;
  const probabilityRiskLevel = scoreBand.riskLevel ?? ERiskLevel.MEDIUM;
  const probability = probabilityAvailable ? score.score.defaultProbability : null;
  const creditLimit = buildCreditLimit(score.score.creditLimit ?? 0, summary?.usedCredit ?? 0);
  const gaugeRiskKey = gaugeRiskLevel.toUpperCase();
  const probabilityRiskKey = probabilityRiskLevel.toUpperCase();

  return {
    scoreId: score.score.scoreId,
    personType: isCompany ? EPersonType.PJ : EPersonType.PF,
    companyName: deriveCompanyName(score, isCompany),
    document: score.score.document,
    score: scoreValue,
    scoreMax: SCORE_MAX,
    scoreUnavailable,
    lastUpdatedAt: DateUtils.formatLongMonthDate(score.score.createdAt),
    riskLevel: gaugeRiskLevel,
    probabilityRiskLevel,
    probabilityAvailable,
    riskLabel: i18n(`FinBankCreditScoreResult.gauge.riskLabel.${gaugeRiskKey}`),
    probabilityPercent: buildProbabilityPercent(probability),
    probabilityLabel: probabilityAvailable ? i18n(`FinBankCreditScoreResult.probability.label.${probabilityRiskKey}`) : '',
    probabilityDescription: buildProbabilityDescription(scoreBand, isCompany, i18n),
    riskAnalysis: buildRiskAnalysis(isCompany, score.score.riskAssessmentCode, i18n),
    creditLimit,
    metricCards: buildMetricCards(summary, isCompany, i18n),
    scoreChartPoints: buildChartPoints(score),
    appointments: buildAppointments(score, i18n),
    appointmentDetailsSections: buildAppointmentDetails(score, i18n, isCompany),
    alerts: deriveAlerts({ summary, usagePercent: creditLimit.usagePercent }, i18n),

  };
}

export function isCompanyDocument(documentType: string): boolean {
  const normalized = (documentType ?? '').toUpperCase();
  return normalized === 'CNPJ' || normalized === 'PJ' || normalized === 'COMPANY';
}

function formatProbabilityValue(value: number): string {
  return Number(value.toFixed(2)).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function buildProbabilityPercent(probability: number | null): string {
  if (probability === null) {
    return '-';
  }
  return `${Math.trunc(probability)}%`;
}

function buildProbabilityDescription(scoreBand: IScoreBand, isCompany: boolean, i18n: i18nFn): string {
  if (scoreBand.descriptionValue === null) {
    return i18n(`FinBankCreditScoreResult.probability.${scoreBand.textKey}`);
  }

  const descriptionKey = isCompany ? 'description.PJ' : 'description.PF';
  return i18n(`FinBankCreditScoreResult.probability.${descriptionKey}`, {
    value: formatProbabilityValue(scoreBand.descriptionValue),
  });
}

function deriveCompanyName(score: IGetLatestScoreByDocumentResponse, isCompany: boolean): string {
  if (isCompany) {
    return score.companyMetadata?.tradeName || score.companyMetadata?.legalName || '';
  }
  return score.personMetadata?.name || '';
}

function buildCreditLimit(total: number, used: number): ICreditLimit {
  const available = Math.max(total - used, 0);
  const usagePercent = total > 0 ? Number(((used / total) * 100).toFixed(1)) : 0;
  return { total, available, used, usagePercent };
}

function buildRiskAnalysis(isCompany: boolean, riskAssessmentCode: string | undefined, i18n: i18nFn): IRiskAnalysis {
  const personTypeKey = isCompany ? 'PJ' : 'PF';
  const personType = i18n(`FinBankCreditScoreResult.riskAnalysis.personType.${personTypeKey}`);
  const riskLevel = deriveBusinessRiskLevel(isCompany, riskAssessmentCode);

  if (riskLevel == null) {
    return {
      personType,
      available: false,
      riskLevel: ERiskLevel.MEDIUM,
      riskLabel: i18n('FinBankCreditScoreResult.riskAnalysis.unavailable.label'),
      riskDescription: i18n('FinBankCreditScoreResult.riskAnalysis.unavailable.description'),
    };
  }

  const riskKey = riskLevel.toUpperCase();
  const riskAnalysis: IRiskAnalysis = {
    personType,
    available: true,
    riskLevel,
    riskLabel: i18n(`FinBankCreditScoreResult.riskAnalysis.label.${personTypeKey}.${riskKey}`),
    riskDescription: i18n(`FinBankCreditScoreResult.riskAnalysis.description.${personTypeKey}.${riskKey}`),
  };

  if (isCompany) {
    riskAnalysis.descriptionPrefix = i18n('FinBankCreditScoreResult.risk.evaluateWithCaution');
  }

  return riskAnalysis;
}

function buildMetricCards(summary: IScoreSummaryResponse | null, isCompany: boolean, i18n: i18nFn): IMetricCard[] {
  return METRIC_CARDS.map(config => {
    const labelSuffix = !isCompany && config.i18nKey === 'averageTicket' ? 'labelPF' : 'label';
    return {
      icon: config.icon,
      label: i18n(`FinBankCreditScoreResult.metricCards.${config.i18nKey}.${labelSuffix}`),
      value: config.getValue(summary, i18n),
      helperText: i18n(`FinBankCreditScoreResult.metricCards.${config.i18nKey}.helper`),
    };
  });
}

function buildChartPoints(response: IGetLatestScoreByDocumentResponse) {
  const timeline = response.scoreTimeline ?? [];
  const includeYear = DateUtils.spansMoreThanOneYear(timeline.map(item => item.date));
  return timeline.map(item => ({
    date: DateUtils.formatShortMonthDate(item.date, undefined, includeYear),
    value: StringUtils.safeParseInt(item.value),
  }));
}

function buildAppointments(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn): IAppointmentCategory[] {
  return APPOINTMENT_CATEGORIES.map(({ icon, key }) => {
    return buildAppointmentCategory({
      categoryKey: key,
      icon,
      scoreResponse: response,
      i18n,
    });
  });
}

function getRecordsForCategory(
  key: AppointmentCategoryKey,
  response: IGetLatestScoreByDocumentResponse
): Array<{ registeredAt: string; amount: number }> {
  if (key === 'debts') return response.scoreRestrictions ?? [];
  if (key === 'civilActions') return response.scoreCivilActions ?? [];
  return response.scoreProtests ?? [];
}

function resolveLatestDate( key: AppointmentCategoryKey, response: IGetLatestScoreByDocumentResponse, records: Array<{ registeredAt: string }>
): string {

  let date;

  switch(key){
    case 'civilActions':
      date = response.civilActionSummary?.mostRecentDate;
      break;
    case 'protests':
      date = response.protestSummary?.mostRecentDate;
      break;
    default:
      date = findLatestDate(records.map(item => item.registeredAt));
  }

  return DateUtils.formatBRDate(date);
}

function resolveTotalValue(key: AppointmentCategoryKey, response: IGetLatestScoreByDocumentResponse, records: Array<{ amount: number }>
): number {

  switch(key){
    case 'protests':
      return response.protestSummary?.totalAmount ?? 0;
    default:
      return records.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  }
}

function resolveTotalCount(key: AppointmentCategoryKey, response: IGetLatestScoreByDocumentResponse, recordsLength: number) {
    switch(key){
      case 'protests':
        return response.protestSummary?.total ?? 0;
      case 'civilActions':
        return response.civilActionSummary?.total ?? recordsLength;
      default:
        return recordsLength;
    }
  }

  function buildAppointmentCategory(params: BuildAppointmentCategoryParams): IAppointmentCategory {
  const { icon, categoryKey, i18n, scoreResponse } = params;

  const records = getRecordsForCategory(categoryKey, scoreResponse);
  const totalCount = records.length;
  const isEmpty = totalCount === 0;
  const status = isEmpty ? EAppointmentStatus.HEALTHY : EAppointmentStatus.ATTENTION;
  const prefix = `FinBankCreditScoreResult.appointments.${categoryKey}`;

  return {
    key: categoryKey,
    icon,
    title: i18n(`${prefix}.title`),
    status,
    statusLabel: i18n(`FinBankCreditScoreResult.appointments.status.${isEmpty ? 'healthy' : 'attention'}`),
    latestDateLabel: i18n(`${prefix}.latestDateLabel`),
    latestDate: resolveLatestDate(categoryKey, scoreResponse, records),
    totalCountLabel: i18n(`${prefix}.totalCountLabel`),
    totalCount: resolveTotalCount(categoryKey, scoreResponse, totalCount),
    totalValueLabel: i18n(`${prefix}.totalValueLabel`),
    totalValue:  resolveTotalValue(categoryKey, scoreResponse, records),
    emptyMessage: isEmpty ? i18n(`${prefix}.emptyMessage`) : undefined
  };
}

function findLatestDate(dates: string[]): string | undefined {
  const valid = dates.filter(Boolean).map(d => new Date(d)).filter(d => !Number.isNaN(d.getTime()));
  const [firstDate, ...remainingDates] = valid;
  if (!firstDate) {
    return undefined;
  }
  return remainingDates.reduce((latest, current) => (current > latest ? current : latest), firstDate).toISOString();
}

function buildAppointmentDetails(response: IGetLatestScoreByDocumentResponse, i18n: i18nFn, isCompany: boolean): IAppointmentDetailSection[] {
  const recentLimit = isCompany ? MAX_RECENT_DETAIL_RECORDS_PJ : MAX_RECENT_DETAIL_RECORDS_PF;

  return [
    buildDetailSection('debts-restrictions', 'debts', response.scoreRestrictions, buildRestrictionRecord, i18n, false, recentLimit),
    buildDetailSection('civil-actions', 'civilActions', response.scoreCivilActions, buildCivilActionRecord, i18n, true, recentLimit),
    buildDetailSection('protests', 'protests', response.scoreProtests, buildProtestRecord, i18n, true, recentLimit),
  ];
}

function buildDetailSection<T extends { registeredAt: string }>(
  id: string,
  categoryKey: AppointmentCategoryKey,
  records: T[] | undefined,
  recordBuilder: (record: T, i18n: i18nFn) => IAppointmentDetailRecord,
  i18n: i18nFn,
  limitToRecent: boolean,
  recentLimit: number,
): IAppointmentDetailSection {
  const prefix = `FinBankCreditScoreResult.appointments.${categoryKey}.detail`;
  const sourceRecords = records ?? [];
  const visibleRecords = limitToRecent
    ? sortByRegisteredAtDesc(sourceRecords).slice(0, recentLimit)
    : sourceRecords;

  const section: IAppointmentDetailSection = {
    id,
    title: i18n(`${prefix}.title`),
    itemsPerPage: 3,
    records: visibleRecords.map(record => recordBuilder(record, i18n)),
  };

  if (limitToRecent) {
    section.subtitle = i18n(`${prefix}.subtitle`, { count: recentLimit });
  }

  return section;
}

function sortByRegisteredAtDesc<T extends { registeredAt: string }>(records: T[]): T[] {
  return [...records].sort((a, b) => registeredAtTime(b.registeredAt) - registeredAtTime(a.registeredAt));
}

function registeredAtTime(value?: string): number {
  const time = new Date(value ?? '').getTime();
  return Number.isNaN(time) ? 0 : time;
}

function buildRestrictionRecord(record: IScoreRestrictionDTO, i18n: i18nFn): IAppointmentDetailRecord {
  const prefix = 'FinBankCreditScoreResult.appointments.debts.detail.fields';
  return {
    fields: [
      { label: i18n(`${prefix}.creditor`), value: record.creditorName ?? '' },
      { label: i18n(`${prefix}.amount`), value: StringUtils.formatBRL(record.amount ?? 0) },
      { label: i18n(`${prefix}.date`), value: DateUtils.formatBRDate(record.registeredAt) },
      { label: i18n(`${prefix}.contract`), value: record.contractNumber ?? '' },
      { label: i18n(`${prefix}.location`), value: record.location ?? '' },
      { label: i18n(`${prefix}.classification`), value: record.classificationType ?? '' },
    ],
  };
}

function buildCivilActionRecord(record: IScoreCivilActionDTO, i18n: i18nFn): IAppointmentDetailRecord {
  const prefix = 'FinBankCreditScoreResult.appointments.civilActions.detail.fields';
  return {
    fields: [
      { label: i18n(`${prefix}.plaintiff`), value: record.plaintiffName ?? '' },
      { label: i18n(`${prefix}.amount`), value: StringUtils.formatBRL(record.amount ?? 0) },
      { label: i18n(`${prefix}.action`), value: record.actionType ?? '' },
      { label: i18n(`${prefix}.date`), value: DateUtils.formatBRDate(record.registeredAt) },
      { label: i18n(`${prefix}.case`), value: record.caseNumber ?? '' },
      { label: i18n(`${prefix}.location`), value: record.location ?? '' },
    ],
  };
}

function buildProtestRecord(record: IScoreProtestDTO, i18n: i18nFn): IAppointmentDetailRecord {
  const prefix = 'FinBankCreditScoreResult.appointments.protests.detail.fields';
  return {
    fields: [
      { label: i18n(`${prefix}.date`), value: DateUtils.formatBRDate(record.registeredAt) },
      { label: i18n(`${prefix}.amount`), value: StringUtils.formatBRL(record.amount ?? 0) },
      { label: i18n(`${prefix}.notary`), value: record.notaryOfficeName ?? '' },
      { label: i18n(`${prefix}.city`), value: record.city ?? '' },
      { label: i18n(`${prefix}.state`), value: record.state ?? '' },
    ],
  };
}